import React, { useState, useEffect, useRef } from 'react';

/* ══ HERO CYCLING STATUS ══  (like Claude's symbol + "Debugging..." line) */
const STATUS_SYMBOLS  = ['◈', '◓', '✽', '◐', '◑', '◒', '●', '◯', '◇', '◆', '▲', '▼'];
const STATUS_MESSAGES = [
  'Scanning options chain…',
  'Calculating GEX levels…',
  'Reading open interest…',
  'Mapping dealer exposure…',
  'Pricing call walls…',
  'Pricing put walls…',
  'Locating gamma flip…',
  'Checking dealer delta…',
  'Synthesizing regime…',
  'Calibrating signals…',
  'Crunching flow data…',
  'Analysis ready.',
];

function StatusCycler({ ready }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!ready) return;
    const t = setInterval(() => setIdx(i => (i + 1) % STATUS_SYMBOLS.length), 1000);
    return () => clearInterval(t);
  }, [ready]);
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '8px',
      fontSize: '13px', color: '#4b4b52',
      fontFamily: "'JetBrains Mono', monospace",
      opacity: ready ? 1 : 0,
      animation: ready ? 'hero-cta-rise 0.5s ease 0.1s both' : 'none',
      marginBottom: '20px', userSelect: 'none',
    }}>
      <span style={{ color: '#34c759', fontSize: '14px', display: 'inline-block', width: '16px', textAlign: 'center' }}>
        {STATUS_SYMBOLS[idx]}
      </span>
      <span style={{ transition: 'opacity 0.2s' }}>{STATUS_MESSAGES[idx]}</span>
    </div>
  );
}

/* ══ TYPEWRITER CYCLE ══  "Built for [retail traders]" */
const TYPEWRITER_WORDS = [
  'gamma traders.',
  'options buyers.',
  'options sellers.',
  'day traders.',
  'swing traders.',
  '0DTE traders.',
  'earnings traders.',
];

function TypewriterCycle({ ready }) {
  const [wordIdx, setWordIdx]     = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [deleting, setDeleting]   = useState(false);

  useEffect(() => {
    if (!ready) return;
    const word = TYPEWRITER_WORDS[wordIdx];
    let timer;
    if (!deleting && displayed.length < word.length) {
      timer = setTimeout(() => setDisplayed(word.slice(0, displayed.length + 1)), 75);
    } else if (!deleting && displayed.length === word.length) {
      timer = setTimeout(() => setDeleting(true), 2000);
    } else if (deleting && displayed.length > 0) {
      timer = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 40);
    } else if (deleting && displayed.length === 0) {
      setDeleting(false);
      setWordIdx(i => (i + 1) % TYPEWRITER_WORDS.length);
    }
    return () => clearTimeout(timer);
  }, [ready, displayed, deleting, wordIdx]);

  return (
    <span style={{ color: '#34c759' }}>
      {displayed}
      <span style={{
        display: 'inline-block', width: '3px', height: '0.85em',
        background: '#34c759', marginLeft: '3px', verticalAlign: 'text-bottom',
        borderRadius: '1px', boxShadow: '0 0 8px rgba(52,199,89,0.6)',
        animation: 'tw-blink 1s step-end infinite',
      }} />
    </span>
  );
}

/* ─────────────────────────────────────────────
   GAMMALENS DESIGN TOKENS
   bg:       #111114  (warm near-black, never pure #000)
   surface:  #1a1a1f  (cards)
   raised:   #222228  (nested / hover)
   border:   rgba(255,255,255,0.07)
   text-1:   #f2f2f7  (primary — Apple off-white)
   text-2:   #98989f  (secondary)
   text-3:   #545459  (muted)
   text-4:   #2c2c30  (very muted)
   up:       #34c759  (positive GEX)
   down:     #ff3b30  (negative GEX)
   pivot:    #bf5af2  (gamma flip)
   amber:    #ff9f0a  (neutral)
   brand:    #f2f2f7  (CTA = white on dark — Vercel-style)
───────────────────────────────────────────── */

/* ══ LOGO ══ */
const GammaLensLogo = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
    <defs>
      <linearGradient id="gl-g" x1="14" y1="32" x2="50" y2="32">
        <stop offset="0%" stopColor="#34c759" />
        <stop offset="100%" stopColor="#30a8ff" />
      </linearGradient>
    </defs>
    <rect width="64" height="64" rx="14" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
    <circle cx="32" cy="32" r="17" stroke="rgba(255,255,255,0.07)" strokeWidth="1.5" fill="none" />
    <line x1="32" y1="11" x2="32" y2="20" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" />
    <line x1="32" y1="44" x2="32" y2="53" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" />
    <line x1="11" y1="32" x2="20" y2="32" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" />
    <line x1="44" y1="32" x2="53" y2="32" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" />
    <path d="M20 36 Q25 36 28 28 Q30 22 32 24 Q34 26 36 34 Q38 40 44 36"
      stroke="url(#gl-g)" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    <circle cx="32" cy="32" r="2.5" fill="#34c759" />
  </svg>
);

/* ══ REVEAL ══ */
const useReveal = () => {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setV(true); o.disconnect(); } }, { threshold: 0.1 });
    o.observe(el); return () => o.disconnect();
  }, []);
  return [ref, v];
};
const Reveal = ({ children, delay = 0, style = {} }) => {
  const [ref, v] = useReveal();
  return (
    <div ref={ref} style={{
      opacity: v ? 1 : 0, transform: v ? 'none' : 'translateY(20px)',
      transition: `opacity 0.55s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.55s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      ...style,
    }}>{children}</div>
  );
};



/* ══ LIVE CANDLE CHART — TradingView style ══ */
const LiveCandleChart = () => {
  const COUNT   = 38;
  const BASE    = 635;
  const velRef  = useRef(0);
  const liveVel = useRef(0);

  const makeCandle = (prev) => {
    // ~10% big impulse candles, ~20% tiny doji/spinning tops, rest normal
    const rng = Math.random();
    const bodyMult = rng < 0.10 ? 3.4 + Math.random() * 2.8   // big spike
                   : rng < 0.30 ? 0.06 + Math.random() * 0.16 // doji / inside bar
                   : 1;
    velRef.current = velRef.current * 0.62 + (Math.random() - 0.5) * 2.6 * bodyMult;
    const close = parseFloat((prev + velRef.current + (BASE - prev) * 0.014).toFixed(2));
    // Wicks scale with body size; doji candles often have long wicks
    const wickMult = bodyMult > 1 ? bodyMult * 0.55 : bodyMult < 0.3 ? 2.2 : 1;
    const range = (Math.random() * 2.0 + 0.5) * wickMult, uw = Math.random() * range;
    return {
      open: prev, close,
      high: parseFloat((Math.max(prev, close) + uw).toFixed(2)),
      low:  parseFloat((Math.min(prev, close) - (range - uw)).toFixed(2)),
      bullish: close >= prev,
      vol: Math.min(0.3 + Math.random() * 0.7 * (bodyMult > 1 ? bodyMult * 0.35 : 1), 1.0),
    };
  };

  const [history, setHistory] = useState(() => {
    velRef.current = 0;
    const arr = []; let p = BASE;
    for (let i = 0; i < COUNT; i++) { const c = makeCandle(p); arr.push(c); p = c.close; }
    return arr;
  });

  const live = useRef(null);
  const [, tick] = useState(0);
  if (!live.current) {
    const lc = history[history.length - 1]?.close ?? BASE;
    live.current = { open: lc, close: lc, high: lc + 0.5, low: lc - 0.5, vol: 0.08 };
  }

  useEffect(() => {
    const priceTimer = setInterval(() => {
      liveVel.current = liveVel.current * 0.68 + (Math.random() - 0.5) * 0.32;
      live.current.close = parseFloat((live.current.close + liveVel.current + (BASE - live.current.close) * 0.007).toFixed(2));
      live.current.high = Math.max(live.current.high, live.current.close);
      live.current.low  = Math.min(live.current.low,  live.current.close);
      live.current.vol  = Math.min(live.current.vol + 0.025, 1.0);
      tick(n => n + 1);
    }, 520);

    const candleTimer = setInterval(() => {
      const closed = { ...live.current, bullish: live.current.close >= live.current.open };
      const no = live.current.close;
      liveVel.current *= 0.55;
      live.current = { open: no, close: no, high: no + 0.3, low: no - 0.3, vol: 0.06 };
      setHistory(prev => [...prev.slice(1), closed]);
    }, 4000);

    return () => { clearInterval(priceTimer); clearInterval(candleTimer); };
  }, []);

  // ── Layout constants ──
  const VW = 760, VH = 268;
  const OHLC_H  = 28;   // top OHLC info row (inside SVG)
  const TIME_H  = 20;   // bottom time axis
  const VOL_H   = 40;   // volume pane
  const R_AXIS  = 64;   // right price axis width
  const L_PAD   = 10;
  const SEP     = 8;    // gap between price pane and volume pane

  const chartTop = OHLC_H;
  const chartBot = VH - TIME_H - VOL_H - SEP;
  const chartH   = chartBot - chartTop;
  const volTop   = chartBot + SEP;
  const volBot   = VH - TIME_H;

  const step = (VW - L_PAD - R_AXIS) / (COUNT + 1);
  const cw   = Math.max(Math.floor(step) - 1, 5);
  const xp   = (i) => L_PAD + i * step;

  // Price scale (anchored to history, stable between ticks)
  const hPrices = history.flatMap(c => [c.high, c.low]);
  const rawMin = Math.min(...hPrices), rawMax = Math.max(...hPrices);
  const margin = (rawMax - rawMin) * 0.2;
  const minP = rawMin - margin, maxP = rawMax + margin;
  const yp   = (p) => chartTop + chartH * (1 - (p - minP) / (maxP - minP));

  // Right-axis price ticks (6 levels)
  const tickCount = 6;
  const priceTicks = Array.from({ length: tickCount }, (_, i) =>
    parseFloat((minP + i * (maxP - minP) / (tickCount - 1)).toFixed(2))
  );

  // Time labels every 5 candles
  const timeTick = (i) => {
    const m = 9 * 60 + 30 + i;
    return `${Math.floor(m / 60)}:${(m % 60).toString().padStart(2, '0')}`;
  };

  const LEVELS = [
    { p: 640, c: '#26a69a', label: 'CW 640' },
    { p: 635, c: '#ab68ff', label: 'γ  635' },
    { p: 629, c: '#ef5350', label: 'PW 629' },
  ];

  const isBullLive = live.current.close >= live.current.open;
  const liveCol    = isBullLive ? '#26a69a' : '#ef5350';

  const all = [...history, { ...live.current, bullish: isBullLive, isLive: true }];

  // Area fill path under close line
  const linePts  = all.map((c, i) => `${i === 0 ? 'M' : 'L'}${(xp(i) + cw / 2).toFixed(1)},${yp(c.close).toFixed(1)}`).join(' ');
  const areaPath = linePts
    + ` L${(xp(all.length - 1) + cw / 2).toFixed(1)},${chartBot}`
    + ` L${(xp(0) + cw / 2).toFixed(1)},${chartBot} Z`;

  // OHLC values for info row
  const lc   = live.current;
  const chg  = lc.close - lc.open;
  const chgPct = ((chg / lc.open) * 100).toFixed(2);
  const chgCol = chg >= 0 ? '#26a69a' : '#ef5350';

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} width="100%" style={{ display: 'block' }} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="tv-bull-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#26a69a" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#26a69a" stopOpacity="0"    />
        </linearGradient>
        <linearGradient id="tv-bear-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#ef5350" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#ef5350" stopOpacity="0"    />
        </linearGradient>
      </defs>

      {/* ── Background ── */}
      <rect x="0" y="0" width={VW} height={VH} fill="#131722" />
      <rect x={L_PAD} y={chartTop} width={VW - L_PAD - R_AXIS} height={chartH} fill="#161b2a" />

      {/* ── OHLC info bar ── */}
      <text x={L_PAD} y={20} fill="#d1d4dc" fontSize="12" fontFamily="JetBrains Mono,monospace" fontWeight="700">SPY</text>
      <text x={L_PAD + 40} y={20} fill="#4c525e" fontSize="10" fontFamily="JetBrains Mono,monospace">1m</text>
      <rect x={L_PAD + 64} y={8} width={36} height={14} rx="3" fill="rgba(38,166,154,0.12)" stroke="rgba(38,166,154,0.35)" strokeWidth="0.5" />
      <text x={L_PAD + 67} y={19} fill="#26a69a" fontSize="9" fontFamily="JetBrains Mono,monospace" fontWeight="600">LIVE</text>
      {[
        { label: 'O', val: lc.open.toFixed(2),  x: L_PAD + 112 },
        { label: 'H', val: lc.high.toFixed(2),  x: L_PAD + 195 },
        { label: 'L', val: lc.low.toFixed(2),   x: L_PAD + 278 },
        { label: 'C', val: lc.close.toFixed(2), x: L_PAD + 361 },
      ].map(({ label, val, x }) => (
        <g key={label}>
          <text x={x}      y={20} fill="#4c525e" fontSize="10" fontFamily="JetBrains Mono,monospace">{label}</text>
          <text x={x + 12} y={20} fill="#d1d4dc" fontSize="10" fontFamily="JetBrains Mono,monospace">{val}</text>
        </g>
      ))}
      <text x={L_PAD + 455} y={20} fill={chgCol} fontSize="10" fontFamily="JetBrains Mono,monospace" fontWeight="600">
        {chg >= 0 ? '+' : ''}{chg.toFixed(2)}  ({chg >= 0 ? '+' : ''}{chgPct}%)
      </text>

      {/* ── Chart border ── */}
      <line x1={VW - R_AXIS} y1={chartTop} x2={VW - R_AXIS} y2={volBot} stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
      <line x1={L_PAD}       y1={chartBot} x2={VW - R_AXIS} y2={chartBot} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
      <line x1={L_PAD}       y1={volBot}   x2={VW - R_AXIS} y2={volBot}   stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />

      {/* ── Horizontal grid lines + price axis ── */}
      {priceTicks.map((p) => {
        const y = yp(p);
        if (y < chartTop || y > chartBot) return null;
        return (
          <g key={p}>
            <line x1={L_PAD} y1={y} x2={VW - R_AXIS} y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="0.5" />
            <text x={VW - R_AXIS + 7} y={y + 3.5} fill="#4c525e" fontSize="9.5" fontFamily="JetBrains Mono,monospace">{p.toFixed(0)}</text>
          </g>
        );
      })}

      {/* ── Gamma level lines (overlaid on grid) ── */}
      {LEVELS.map(({ p, c, label }) => {
        const ly = yp(p);
        if (ly < chartTop || ly > chartBot) return null;
        return (
          <g key={p}>
            <line x1={L_PAD} y1={ly} x2={VW - R_AXIS} y2={ly} stroke={c} strokeWidth="0.85" strokeDasharray="4,5" opacity="0.55" />
            <text x={VW - R_AXIS + 7} y={ly + 3.5} fill={c} fontSize="9.5" fontFamily="JetBrains Mono,monospace" fontWeight="600" opacity="0.8">{label}</text>
          </g>
        );
      })}

      {/* ── Price area gradient ── */}
      <path d={areaPath} fill={`url(#tv-${isBullLive ? 'bull' : 'bear'}-area)`} />

      {/* ── Volume separator ── */}
      <line x1={L_PAD} y1={volTop - 2} x2={VW - R_AXIS} y2={volTop - 2} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
      <text x={L_PAD + 3} y={volTop + 11} fill="#2c313d" fontSize="8" fontFamily="JetBrains Mono,monospace">VOLUME</text>

      {/* ── Volume bars ── */}
      {all.map((c, i) => {
        const maxH = VOL_H - 8;
        const h = Math.max((c.vol ?? 0.5) * maxH, 1.5);
        const col = c.bullish ? '#26a69a' : '#ef5350';
        return <rect key={i} x={xp(i)} y={volBot - h} width={cw} height={h}
          fill={col} opacity={c.isLive ? 0.6 : 0.32} rx="0.5" />;
      })}

      {/* ── Historical candles ── */}
      {history.map((c, i) => {
        const cx = xp(i);
        const bodyTop = Math.min(yp(c.open), yp(c.close));
        const bodyH   = Math.max(Math.abs(yp(c.close) - yp(c.open)), 1.5);
        const col = c.bullish ? '#26a69a' : '#ef5350';
        return (
          <g key={i}>
            <line x1={cx + cw / 2} y1={yp(c.high)} x2={cx + cw / 2} y2={yp(c.low)} stroke={col} strokeWidth="1" />
            <rect x={cx} y={bodyTop} width={cw} height={bodyH} fill={col} rx="0.5" opacity="0.9" />
          </g>
        );
      })}

      {/* ── Live candle (smooth transitions) ── */}
      {(() => {
        const cx      = xp(COUNT);
        const bodyTop = Math.min(yp(lc.open), yp(lc.close));
        const bodyH   = Math.max(Math.abs(yp(lc.close) - yp(lc.open)), 1.5);
        const ease    = 'all 0.42s cubic-bezier(0.4,0,0.2,1)';
        return (
          <g>
            <rect x={cx - 2} y={bodyTop - 2} width={cw + 4} height={bodyH + 4}
              fill="none" stroke={liveCol} strokeWidth="1.5" rx="2" opacity="0.3" />
            <line x1={cx + cw / 2} y1={yp(lc.high)} x2={cx + cw / 2} y2={yp(lc.low)}
              stroke={liveCol} strokeWidth="1.2" style={{ transition: ease }} />
            <rect x={cx} y={bodyTop} width={cw} height={bodyH} fill={liveCol} rx="0.5" style={{ transition: ease }} />
          </g>
        );
      })()}

      {/* ── Live price horizontal + tag ── */}
      {(() => {
        const ly = yp(lc.close);
        if (ly < chartTop || ly > chartBot) return null;
        return (
          <g>
            <line x1={L_PAD} y1={ly} x2={VW - R_AXIS} y2={ly}
              stroke={liveCol} strokeWidth="0.6" strokeDasharray="2,3" opacity="0.5" />
            <rect x={VW - R_AXIS + 1} y={ly - 9} width={R_AXIS - 5} height={16} rx="3"
              fill={isBullLive ? '#26a69a' : '#ef5350'}
              stroke="none" />
            <text x={VW - R_AXIS + 6} y={ly + 4} fill="#ffffff" fontSize="10" fontFamily="JetBrains Mono,monospace" fontWeight="700">
              {lc.close.toFixed(2)}
            </text>
          </g>
        );
      })()}

      {/* ── Time axis ── */}
      {[0, 5, 10, 15, 20, 25, 30, 35].map(idx => {
        const x = xp(idx) + cw / 2;
        if (x > VW - R_AXIS - 24) return null;
        return (
          <g key={idx}>
            <line x1={x} y1={volBot} x2={x} y2={volBot + 4} stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
            <text x={x} y={volBot + 16} fill="#2c313d" fontSize="9" fontFamily="JetBrains Mono,monospace" textAnchor="middle">
              {timeTick(idx)}
            </text>
          </g>
        );
      })}

      {/* ── Bottom fade — chart sinks into dark ── */}
      <defs>
        <linearGradient id="chart-bottom-fade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#131722" stopOpacity="0"    />
          <stop offset="100%" stopColor="#131722" stopOpacity="0.97" />
        </linearGradient>
      </defs>
      <rect x="0" y={VH * 0.44} width={VW} height={VH * 0.56} fill="url(#chart-bottom-fade)" />
    </svg>
  );
};

/* ══ FAQ ══ */
const FAQ = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid rgba(255,255,255,0.09)' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', padding: '17px 0', background: 'none', border: 'none',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        cursor: 'pointer', textAlign: 'left', gap: '16px',
      }}>
        <span style={{ fontSize: '14px', fontWeight: 500, color: open ? '#f2f2f7' : '#98989f', lineHeight: 1.4, transition: 'color 0.2s' }}>{q}</span>
        <span style={{ color: open ? '#f2f2f7' : '#a1a1aa', fontSize: '20px', transition: 'transform 0.3s ease, color 0.2s', transform: open ? 'rotate(45deg)' : 'none', flexShrink: 0 }}>+</span>
      </button>
      <div style={{ maxHeight: open ? '220px' : 0, overflow: 'hidden', transition: 'max-height 0.4s cubic-bezier(0.16,1,0.3,1)' }}>
        <p style={{ fontSize: '13px', color: '#98989f', lineHeight: 1.85, margin: 0, paddingBottom: '18px' }}>{a}</p>
      </div>
    </div>
  );
};


/* ══ LANDING PAGE ══ */
export default function LandingPage({ onGetStarted, onSignIn }) {
  const [ready, setReady] = useState(false);
  useEffect(() => { const t = setTimeout(() => setReady(true), 80); return () => clearTimeout(t); }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', color: '#f2f2f7', fontFamily: "'Plus Jakarta Sans Variable', -apple-system, system-ui, sans-serif", overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap');
        *,*::before,*::after{box-sizing:border-box;}
        @keyframes lp-spin{to{transform:rotate(360deg)}}
        @keyframes lp-dot{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(.75)}}
        @keyframes aurora-1{0%,100%{transform:translate(0,0) scale(1);opacity:.7}33%{transform:translate(40px,-50px) scale(1.06);opacity:1}66%{transform:translate(-25px,25px) scale(.94);opacity:.8}}
        @keyframes aurora-2{0%,100%{transform:translate(0,0) scale(1);opacity:.6}50%{transform:translate(-50px,-35px) scale(1.1);opacity:.9}}
        @keyframes aurora-3{0%,100%{transform:translate(0,0) scale(1);opacity:.5}40%{transform:translate(30px,40px) scale(.9);opacity:.8}}
        @keyframes sc-mask-in{from{transform:translateY(110%) skewY(3deg)}to{transform:translateY(0) skewY(0deg)}}
        @keyframes hero-word-pop{0%{transform:translateY(110%) skewY(3deg) scale(0.92)}65%{transform:translateY(-3%) skewY(-0.5deg) scale(1.03)}100%{transform:translateY(0) skewY(0deg) scale(1)}}
        @keyframes hero-badge-in{from{opacity:0;transform:translateY(-12px) scale(0.93)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes hero-line-draw{from{transform:scaleX(0);opacity:0}to{transform:scaleX(1);opacity:1}}
        @keyframes hero-accent-glow{0%{text-shadow:none}50%{text-shadow:0 0 32px rgba(52,199,89,0.6),0 0 64px rgba(52,199,89,0.2)}100%{text-shadow:0 0 12px rgba(52,199,89,0.22)}}
        @keyframes hero-bloom{from{opacity:0;transform:translateX(-50%) scale(0.4)}to{opacity:1;transform:translateX(-50%) scale(1)}}
        @keyframes hero-cta-rise{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes hero-btn-pulse{0%,100%{box-shadow:0 0 0 0 rgba(242,242,247,0)}60%{box-shadow:0 0 0 6px rgba(242,242,247,0.08)}}
        @keyframes hero-shimmer{from{transform:translateX(-180%) skewX(-18deg)}to{transform:translateX(480%) skewX(-18deg)}}
        @keyframes hero-sub-word{from{opacity:0;transform:translateY(10px) blur(4px)}to{opacity:1;transform:translateY(0) blur(0)}}
        @keyframes hero-char-in{0%{opacity:0;transform:translateY(60%) scaleY(0.6)}60%{transform:translateY(-4%) scaleY(1.06)}100%{opacity:1;transform:translateY(0) scaleY(1)}}
        @keyframes tw-blink{0%,100%{opacity:1}50%{opacity:0}}
        ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.15);border-radius:4px}::-webkit-scrollbar-track{background:transparent}
        .lp-chart-3d:hover{transform:rotateX(2deg) rotateY(-1deg)!important;box-shadow:0 40px 90px rgba(0,0,0,0.65),0 12px 32px rgba(0,0,0,0.35),inset 0 1px 0 rgba(255,255,255,0.07),0 0 60px rgba(38,166,154,0.08)!important}
        .gl-btn-primary{background:#f2f2f7;color:#111114;transition:background .15s,box-shadow .15s}
        .gl-btn-primary:hover{background:#ffffff;box-shadow:0 0 0 4px rgba(242,242,247,.12)}
        .gl-btn-ghost{border:1px solid rgba(255,255,255,.14);color:#d4d4d8;transition:border-color .15s,color .15s}
        .gl-btn-ghost:hover{border-color:rgba(255,255,255,.28);color:#ffffff}
        .gl-btn-sm{transition:background .15s}
        .gl-btn-sm:hover{background:#ffffff !important}
        .gl-nav-link{color:#a1a1aa;transition:color .15s}
        .gl-nav-link:hover{color:#f2f2f7}
        .gl-feat{background:#161618;border:1px solid rgba(255,255,255,.09);transition:border-color .2s,background .2s}
        .gl-feat:hover{border-color:rgba(255,255,255,.18);background:#1c1c1f}
        .gl-step{background:#161618;border:1px solid rgba(255,255,255,.09);transition:border-color .2s,background .2s}
        .gl-step:hover{border-color:rgba(255,255,255,.18);background:#1c1c1f}
        @media(max-width:960px){
          .lp-two-col{grid-template-columns:1fr!important;gap:32px!important}
          .lp-four-col{grid-template-columns:1fr 1fr!important}
          .lp-three-col{grid-template-columns:repeat(3,1fr)!important}
          .lp-hero-h1{font-size:clamp(40px,8vw,64px)!important;letter-spacing:-2.5px!important}
          .lp-wrap{padding:0 20px!important}
          .lp-nav{padding:0 16px!important}
          .lp-showcase-stats{padding:28px 24px 24px!important;grid-template-columns:repeat(2,1fr)!important;gap:16px 0!important}
          .lp-showcase-features{padding:24px 24px 28px!important}
          .lp-divider{margin:0 24px!important}
          .lp-stat-num{font-size:40px!important;letter-spacing:-2px!important}
          .lp-stat-item{border-left:none!important;padding-left:0!important}
          .lp-stat-item:nth-child(even){padding-left:24px!important;border-left:1px solid rgba(255,255,255,0.07)!important}
        }
        @media(max-width:640px){
          .lp-four-col{grid-template-columns:1fr!important}
          .lp-three-col{grid-template-columns:1fr!important}
          .lp-hero-h1{font-size:clamp(38px,10vw,52px)!important;letter-spacing:-2px!important}
          .lp-hero-sub{font-size:14px!important;letter-spacing:-0.01em!important}
          .lp-cta-row{flex-direction:column!important;align-items:center!important}
          .lp-cta-row button{width:100%!important;max-width:320px!important;text-align:center!important;justify-content:center!important}
          .lp-wrap{padding:0 16px!important}
          .lp-hero-section{padding-top:64px!important}
          .lp-section{padding-top:64px!important;padding-bottom:64px!important}
          .lp-cta-card{padding:44px 24px!important}
          .lp-showcase{padding-bottom:64px!important;padding-top:48px!important}
          .lp-nav-signin{display:none!important}
          .lp-sticky-faq{position:static!important}
          .lp-faq-grid{grid-template-columns:1fr!important;gap:32px!important}
          .lp-chart-3d{transform:rotateX(3deg) rotateY(0deg)!important}
          .lp-showcase-stats{padding:20px 16px 16px!important;gap:12px 0!important;grid-template-columns:repeat(2,1fr)!important}
          .lp-showcase-features{padding:16px 16px 20px!important}
          .lp-divider{margin:0 16px!important}
          .lp-chart-levels{display:none!important}
          .lp-chart-toolbar{flex-wrap:wrap!important;gap:6px!important;padding:6px 10px!important}
          .lp-stat-item{padding-left:0!important;padding-right:0!important;border-left:none!important;border-top:none!important;padding-top:0!important;margin-top:0!important}
          .lp-stat-item:nth-child(even){padding-left:16px!important;border-left:1px solid rgba(255,255,255,0.07)!important}
          .lp-stat-item:nth-child(n+3){padding-top:12px!important;border-top:1px solid rgba(255,255,255,0.07)!important}
          .lp-stat-num{font-size:34px!important;letter-spacing:-1.5px!important}
          .lp-stat-desc{display:none!important}
        }
        @media(max-width:400px){
          .lp-hero-h1{font-size:30px!important;letter-spacing:-1px!important}
          .lp-wrap{padding:0 14px!important}
          .lp-stat-num{font-size:28px!important}
          .lp-stat-label{font-size:11px!important}
          .lp-footer{padding:16px 16px!important}
        }
      `}</style>

      {/* Aurora animated background */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-250px', right: '-50px', width: '700px', height: '700px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(52,199,89,.045) 0%,transparent 65%)', filter: 'blur(80px)', animation: 'aurora-1 20s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: '5%', left: '-150px', width: '600px', height: '600px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(48,168,255,.035) 0%,transparent 65%)', filter: 'blur(80px)', animation: 'aurora-2 26s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', top: '35%', left: '28%', width: '500px', height: '500px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(191,90,242,.02) 0%,transparent 65%)', filter: 'blur(70px)', animation: 'aurora-3 34s ease-in-out infinite reverse' }} />
      </div>

      {/* ══════ NAV ══════ */}
      <nav className="lp-nav" style={{
        position: 'sticky', top: 0, zIndex: 100,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0 40px', height: '58px',
        background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(24px)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <GammaLensLogo size={26} />
          <span style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '-0.3px', color: '#f2f2f7', fontFamily: "'Plus Jakarta Sans Variable', system-ui, sans-serif" }}>GammaLens</span>
          <span style={{ fontSize: '10px', color: '#34c759', background: 'rgba(52,199,89,0.08)', border: '1px solid rgba(52,199,89,0.2)', borderRadius: '5px', padding: '2px 8px', letterSpacing: '0.3px' }}>Beta</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button className="gl-nav-link lp-nav-signin" onClick={onSignIn || onGetStarted} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontFamily: 'inherit', fontSize: '13px', fontWeight: 500, borderRadius: '8px' }}>Sign in</button>
          <button className="gl-btn-primary" onClick={onGetStarted} style={{ padding: '8px 20px', background: '#f2f2f7', border: 'none', borderRadius: '9px', color: '#111114', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Get Started</button>
        </div>
      </nav>

      <div className="lp-wrap" style={{ maxWidth: '1160px', margin: '0 auto', padding: '0 40px', position: 'relative', zIndex: 5 }}>

        {/* ══════ HERO ══════ */}
        <section className="lp-hero-section" style={{ paddingTop: '120px', paddingBottom: '0', textAlign: 'center', position: 'relative' }}>

          {/* Center bloom — expands on load behind headline */}
          <div style={{
            position: 'absolute', top: '-40px', left: '50%', width: '800px', height: '560px',
            borderRadius: '50%', pointerEvents: 'none', zIndex: 0,
            background: 'radial-gradient(ellipse at 50% 40%, rgba(52,199,89,0.07) 0%, rgba(48,168,255,0.03) 45%, transparent 70%)',
            filter: 'blur(48px)',
            animation: ready ? 'hero-bloom 2.2s cubic-bezier(0.16,1,0.3,1) 0s both' : 'none',
          }} />

          <div style={{ position: 'relative', zIndex: 1 }}>

            {/* 1. Cycling status (symbol + message) */}
            <StatusCycler ready={ready} />

            {/* 2. Main headline — "Built for [typewriter]" */}
            <h1 className="lp-hero-h1" style={{
              fontSize: 'clamp(42px,5.5vw,72px)', fontWeight: 800,
              letterSpacing: '-0.04em', lineHeight: 1.0,
              margin: '0 auto 0', maxWidth: '900px',
              fontFamily: "'Plus Jakarta Sans Variable', system-ui, sans-serif",
              color: '#f2f2f7',
              opacity: ready ? 1 : 0,
              animation: ready ? 'hero-cta-rise 0.7s cubic-bezier(0.16,1,0.3,1) 0.2s both' : 'none',
            }}>
              <span style={{ color: 'rgba(242,242,247,0.55)' }}>Built for </span>
              <TypewriterCycle ready={ready} />
            </h1>

            {/* 3. Sub-headline — "Read the dealer. Trade the level." */}
            <div style={{ position: 'relative', display: 'inline-block', width: '100%', marginTop: '24px' }}>
              <p className="lp-hero-sub" style={{
                fontSize: 'clamp(18px,2.2vw,32px)', fontWeight: 600,
                letterSpacing: '-0.03em', lineHeight: 1.1,
                margin: '0 auto', maxWidth: '900px',
                fontFamily: "'Plus Jakarta Sans Variable', system-ui, sans-serif",
                color: 'rgba(242,242,247,0.3)',
                opacity: ready ? 1 : 0,
                animation: ready ? 'hero-cta-rise 0.7s cubic-bezier(0.16,1,0.3,1) 0.45s both' : 'none',
              }}>
                Read the{' '}
                <span style={{ color: 'rgba(52,199,89,0.7)' }}>dealer.</span>
                {' '}Trade the{' '}
                <span style={{ color: 'rgba(52,199,89,0.7)' }}>level.</span>
              </p>
            </div>

            {/* Accent line */}
            <div style={{
              height: '1px', width: '56px', margin: '32px auto',
              background: 'linear-gradient(90deg, transparent, #34c759, rgba(52,199,89,0.25), transparent)',
              transformOrigin: 'center',
              animation: ready ? 'hero-line-draw 1s cubic-bezier(0.16,1,0.3,1) 0.7s both' : 'none',
              opacity: ready ? undefined : 0,
            }} />

            {/* Subtitle */}
            <div style={{ margin: '0 auto 48px', maxWidth: '500px' }}>
              <p style={{
                fontSize: 'clamp(15px,1.6vw,18px)', color: '#98989f',
                margin: 0, lineHeight: 1.75, fontWeight: 400,
                opacity: ready ? 1 : 0,
                animation: ready ? 'hero-sub-word 0.6s cubic-bezier(0.16,1,0.3,1) 0.8s both' : 'none',
              }}>
                Call walls, put walls, gamma flip — dealer positioning across 100+ liquid tickers. Free.
              </p>
            </div>

            {/* CTAs */}
            <div className="lp-cta-row" style={{
              display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap',
              animation: ready ? 'hero-cta-rise 0.7s cubic-bezier(0.16,1,0.3,1) 1.1s both' : 'none',
              opacity: ready ? undefined : 0,
            }}>
              <button className="gl-btn-primary" onClick={onGetStarted} style={{
                padding: '15px 40px', background: '#f2f2f7', border: 'none', borderRadius: '14px',
                color: '#111114', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                fontFamily: 'inherit', letterSpacing: '-.02em',
                animation: ready ? 'hero-btn-pulse 2s ease 1.8s 2' : 'none',
              }}>
                Open Dashboard — Free
              </button>
              <button className="gl-btn-ghost" onClick={onSignIn || onGetStarted} style={{
                padding: '15px 26px', background: 'transparent', borderRadius: '14px',
                fontSize: '15px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
              }}>
                Sign in →
              </button>
            </div>

            <p style={{
              fontSize: '12px', color: '#3f3f46', marginTop: '18px', letterSpacing: '0.2px',
              animation: ready ? 'hero-cta-rise 0.6s ease 1.25s both' : 'none',
              opacity: ready ? undefined : 0,
            }}>
              No credit card · No sign-up required · Free during beta
            </p>

          </div>

        </section>

        {/* ══════ SHOWCASE ══════ */}
        <section className="lp-showcase" style={{ paddingTop: '72px', paddingBottom: '100px' }}>
          <Reveal delay={0.05}>
            <div style={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(17,17,20,0.6)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)', position: 'relative' }}>
              {/* Gradient top accent */}
              <div style={{ height: '1px', background: 'linear-gradient(90deg,#34c759 0%,#30a8ff 55%,transparent 100%)' }} />

              {/* Stats row */}
              <div className="lp-three-col lp-showcase-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', padding: '40px 48px 36px', gap: '0' }}>
                {[
                  { n: '100+', u: 'Tickers', d: 'ETFs, mega caps, crypto, meme stocks — all major sectors' },
                  { n: '200+', u: 'Traders', d: 'Traders using GammaLens for daily analysis' },
                  { n: '4',  u: 'Key Levels', d: 'Call wall · Put wall · Gamma flip · Net GEX per ticker' },
                  { n: '∞',  u: 'Free to start', d: 'Core GEX levels always free — no card, no trial' },
                ].map((s, i) => (
                  <div key={i} className="lp-stat-item" style={{ paddingLeft: i === 0 ? '0' : '36px', paddingRight: i === 3 ? '0' : '36px', borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.07)' : 'none' }}>
                    <div className="lp-stat-num" style={{ fontSize: '52px', fontWeight: 900, fontFamily: "'JetBrains Mono',monospace", letterSpacing: '-3px', lineHeight: 1, background: 'linear-gradient(135deg,#34c759,#30a8ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{s.n}</div>
                    <div className="lp-stat-label" style={{ fontSize: '13px', fontWeight: 600, color: '#f2f2f7', margin: '10px 0 4px', letterSpacing: '-0.1px' }}>{s.u}</div>
                    <div className="lp-stat-desc" style={{ fontSize: '12px', color: '#71717a', lineHeight: 1.6 }}>{s.d}</div>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="lp-divider" style={{ height: '1px', background: 'rgba(255,255,255,0.07)', margin: '0 48px' }} />

              {/* Features + Chart row */}
              <div className="lp-two-col lp-showcase-features" style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: '40px', alignItems: 'center', padding: '40px 40px 40px' }}>
                {/* Left: text + CTA */}
                <div>
                  <div style={{ fontSize: '10px', color: '#34c759', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '14px', fontWeight: 600 }}>What you get</div>
                  <h3 style={{ fontSize: 'clamp(20px,2vw,28px)', fontWeight: 700, color: '#f2f2f7', letterSpacing: '-0.04em', lineHeight: 1.15, margin: '0 0 10px', fontFamily: "'Plus Jakarta Sans Variable', system-ui, sans-serif" }}>
                    See what dealers<br />see. For free.
                  </h3>
                  <p style={{ fontSize: '13px', color: '#71717a', lineHeight: 1.75, margin: '0 0 24px' }}>
                    Real-time gamma exposure levels across every major ticker — the same data professional desks use to hedge their books.
                  </p>
                  <button onClick={onGetStarted} className="gl-btn-primary" style={{ padding: '11px 26px', background: '#f2f2f7', border: 'none', borderRadius: '10px', color: '#111114', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '-0.02em' }}>
                    Open Dashboard — Free
                  </button>
                  <p style={{ fontSize: '11px', color: '#3f3f46', marginTop: '10px' }}>No sign-up required to view</p>
                </div>

                {/* Right: live chart — 3D tilt */}
                <div style={{ perspective: '1200px', width: '100%', position: 'relative' }}>
                  {/* Ambient glow halo */}
                  <div style={{ position: 'absolute', inset: '-60px', background: 'radial-gradient(ellipse at 55% 45%, rgba(38,166,154,0.13) 0%, rgba(48,168,255,0.07) 40%, transparent 70%)', filter: 'blur(36px)', pointerEvents: 'none', zIndex: 0 }} />
                <div className="lp-chart-3d" style={{ borderRadius: '14px', overflow: 'hidden', background: '#131722', boxShadow: '0 32px 72px rgba(0,0,0,0.55), 0 8px 24px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)', position: 'relative', width: '100%', zIndex: 1, transform: 'rotateX(8deg) rotateY(-5deg)', transformOrigin: 'center center', transformStyle: 'preserve-3d', transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1), box-shadow 0.6s ease' }}>
                  {/* Top accent line */}
                  <div style={{ height: '1px', background: 'linear-gradient(90deg, rgba(38,166,154,0.6) 0%, rgba(48,168,255,0.4) 50%, transparent 100%)' }} />
                  <div className="lp-chart-toolbar" style={{ padding: '7px 12px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '6px', background: '#0f1119' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '8px' }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#26a69a', animation: 'lp-dot 2s ease infinite' }} />
                      <span style={{ fontSize: '11px', fontFamily: "'JetBrains Mono',monospace", color: '#d1d4dc', fontWeight: 700 }}>SPY</span>
                      <span style={{ fontSize: '10px', fontFamily: "'JetBrains Mono',monospace", color: '#363a45' }}>NYSE</span>
                    </div>
                    {['1m', '5m', '15m', '1h', '1D'].map((tf, i) => (
                      <button key={tf} style={{ padding: '2px 7px', background: i === 0 ? 'rgba(38,166,154,0.15)' : 'transparent', border: i === 0 ? '1px solid rgba(38,166,154,0.35)' : '1px solid transparent', borderRadius: '4px', color: i === 0 ? '#26a69a' : '#363a45', fontSize: '10px', fontFamily: "'JetBrains Mono',monospace", cursor: 'pointer', fontWeight: i === 0 ? 700 : 400 }}>
                        {tf}
                      </button>
                    ))}
                    <div className="lp-chart-levels" style={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
                      {[{ l: 'CW', v: '640', c: '#26a69a' }, { l: 'γ', v: '635', c: '#ab68ff' }, { l: 'PW', v: '629', c: '#ef5350' }].map(({ l, v, c }) => (
                        <span key={l} style={{ fontSize: '10px', fontFamily: "'JetBrains Mono',monospace", color: '#363a45' }}>
                          {l} <span style={{ color: c, fontWeight: 700 }}>${v}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ padding: '0' }}>
                    <LiveCandleChart />
                  </div>
                </div>
                </div>
              </div>

            </div>
          </Reveal>
        </section>

        {/* ══════ PRODUCT SHOWCASE ══════ */}
        <ProductShowcaseSection onGetStarted={onGetStarted} />

        {/* ══════ FAQ ══════ */}
        <section className="lp-section" style={{ paddingTop: '96px', paddingBottom: '96px', borderTop: '1px solid rgba(255,255,255,0.09)' }}>
          <div className="lp-two-col lp-faq-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '80px', alignItems: 'start' }}>
            <Reveal>
              <div className="lp-sticky-faq" style={{ position: 'sticky', top: '80px' }}>
                <div style={{ fontSize: '10px', color: '#a1a1aa', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '16px' }}>FAQ</div>
                <h2 style={{ fontSize: 'clamp(22px,2.5vw,34px)', fontWeight: 700, letterSpacing: '-0.04em', color: '#f2f2f7', margin: '0 0 14px', lineHeight: 1.15, fontFamily: "'Plus Jakarta Sans Variable', system-ui, sans-serif" }}>Common<br />questions</h2>
                <p style={{ fontSize: '13px', color: '#98989f', lineHeight: 1.75, margin: '0 0 24px' }}>The dashboard is free and takes 10 seconds to open.</p>
                <button className="gl-btn-ghost" onClick={onGetStarted} style={{ padding: '9px 20px', background: 'transparent', borderRadius: '9px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}>Try it now →</button>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div>
                {[
                  { q: 'Why is this free?', a: "GammaLens is in early access. The full platform with 100+ tickers, GEX/DEX levels, AI-powered analysis, and watchlist is free during this period. Premium features like alerts, historical replay, and advanced analytics are on the roadmap." },
                  { q: 'How is this different from other GEX tools?', a: 'Most GEX tools charge $50–250/month and treat their methodology as a black box. GammaLens shows raw dealer positioning with plain English explanations you can act on immediately.' },
                  { q: 'Do I need to know options to use this?', a: '"Call Wall at $585 = resistance ceiling." That\'s all you need to know. The Learn GEX tab explains every concept in plain language, and AI Insights converts positioning into a structured session read.' },
                  { q: 'How often is the data updated?', a: 'Options chain data is fetched at market open and refreshed every 15 minutes during market hours (9:30–4:00 ET). Off-hours sessions show the most recent end-of-day snapshot.' },

                  { q: 'Why not real-time, second-by-second data?', a: "GEX is derived from the options chain — specifically open interest and dealer delta hedging positions. Open interest doesn't update tick-by-tick; it reflects end-of-day settlement data from the OCC. Intraday options flow shifts gradually, so a 15-minute snapshot captures meaningful regime changes without chasing noise. Professional desks run GEX models on the same cadence — the key levels (call wall, put wall, gamma flip) rarely shift more than a few dollars between refreshes. If you're scalping seconds, GEX isn't your tool. If you're positioning for the session, 15-minute data is exactly right." },
                  { q: 'Can I use GammaLens on mobile?', a: 'Yes. Open gammalens.markets on your phone browser, then add it to your home screen for an app-like experience. No download needed.' },
                  { q: 'Is this financial advice?', a: 'No. GammaLens provides educational market structure analysis only. GEX levels describe mechanical dealer hedging behaviour — always do your own research before trading.' },
                ].map((f, i) => <FAQ key={i} {...f} />)}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ══════ CTA BLOCK ══════ */}
        <Reveal>
          <section style={{ paddingBottom: '120px' }}>
            <div className="lp-cta-card" style={{
              background: 'rgba(10,10,12,0.55)',
              backdropFilter: 'blur(40px)', WebkitBackdropFilter: 'blur(40px)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '28px',
              boxShadow: '0 0 0 1px rgba(52,199,89,0.06), 0 24px 80px rgba(0,0,0,0.6), 0 0 120px rgba(52,199,89,0.04), inset 0 1px 0 rgba(255,255,255,0.07)',
              padding: '80px 48px', textAlign: 'center', position: 'relative', overflow: 'hidden',
            }}>
              {/* Shimmer top border */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent 0%, rgba(52,199,89,0.6) 30%, rgba(180,255,180,0.8) 50%, rgba(52,199,89,0.6) 70%, transparent 100%)', backgroundSize: '200% auto', animation: 'royal-shimmer 3.5s linear infinite' }} />
              {/* Ambient glow top */}
              <div style={{ position: 'absolute', top: '-120px', left: '50%', transform: 'translateX(-50%)', width: '600px', height: '400px', borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(52,199,89,0.08) 0%, transparent 65%)', filter: 'blur(40px)', pointerEvents: 'none' }} />
              {/* Ambient glow bottom */}
              <div style={{ position: 'absolute', bottom: '-80px', left: '50%', transform: 'translateX(-50%)', width: '400px', height: '300px', borderRadius: '50%', background: 'radial-gradient(ellipse, rgba(48,168,255,0.05) 0%, transparent 65%)', filter: 'blur(40px)', pointerEvents: 'none' }} />

              <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Label */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', marginBottom: '28px', padding: '4px 14px', borderRadius: '100px', background: 'rgba(52,199,89,0.07)', border: '1px solid rgba(52,199,89,0.18)' }}>
                  <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#34c759', boxShadow: '0 0 6px #34c759', animation: 'lp-dot 2s ease infinite' }} />
                  <span style={{ fontSize: '10px', fontWeight: 700, color: '#34c759', letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: "'JetBrains Mono',monospace" }}>Free Access</span>
                </div>

                {/* Headline with shimmer gradient */}
                <h2 style={{
                  fontSize: 'clamp(28px,3.2vw,52px)', fontWeight: 800,
                  letterSpacing: '-0.04em', lineHeight: 1.1,
                  margin: '0 0 20px',
                  fontFamily: "'Plus Jakarta Sans Variable', system-ui, sans-serif",
                  background: 'linear-gradient(135deg, #ffffff 0%, #f2f2f7 35%, #b8f5c8 65%, #f2f2f7 100%)',
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
                  animation: 'royal-shimmer 6s linear infinite',
                }}>
                  Know where the dealers<br />stand before the bell
                </h2>

                <p style={{ fontSize: '15px', color: '#71717a', maxWidth: '360px', margin: '0 auto 40px', lineHeight: 1.7 }}>
                  Join traders reading dealer positioning every session.
                </p>

                <button className="gl-btn-primary" onClick={onGetStarted} style={{
                  padding: '15px 48px', background: 'linear-gradient(135deg, #f2f2f7 0%, #e8ffe8 100%)',
                  border: 'none', borderRadius: '14px', color: '#0a0a0a',
                  fontSize: '15px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
                  letterSpacing: '-0.02em',
                  boxShadow: '0 0 0 1px rgba(255,255,255,0.1), 0 4px 24px rgba(52,199,89,0.2)',
                  transition: 'box-shadow 0.2s, background 0.2s',
                }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 0 3px rgba(52,199,89,0.3), 0 8px 32px rgba(52,199,89,0.25)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 0 0 1px rgba(255,255,255,0.1), 0 4px 24px rgba(52,199,89,0.2)'; }}
                >
                  Open Dashboard — Free
                </button>
                <p style={{ fontSize: '12px', color: '#3f3f46', marginTop: '16px' }}>No credit card · No sign-up required</p>
              </div>
            </div>
          </section>
        </Reveal>
      </div>

      {/* ══════ FOOTER ══════ */}
      <footer className="lp-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.09)', padding: '20px 40px' }}>
        <div style={{ maxWidth: '1160px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GammaLensLogo size={16} />
            <span style={{ fontSize: '12px', color: '#71717a' }}>GammaLens © {new Date().getFullYear()}</span>
          </div>
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', color: '#71717a' }}>Not financial advice</span>
            <button onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'legal' }))} style={{ background: 'none', border: 'none', color: '#71717a', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px', fontFamily: 'inherit' }}>Terms & Privacy</button>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   PRODUCT SHOWCASE — auto-cycling animated demo
══════════════════════════════════════════════════ */

const SHOWCASE_TABS = [
  {
    id: 'overview',
    label: 'GEX Snapshot',
    insight: 'One screen. Every ticker. Regime at a glance.',
    stats: [
      { val: 10,  dec: 0, pre: '',  suf: '',   label: 'tickers covered', color: '#34c759' },
      { val: 100, dec: 0, pre: '',  suf: '%',   label: 'free access',     color: '#30a8ff' },
      { val: 4,   dec: 0, pre: '',  suf: '',   label: 'key levels each',  color: '#bf5af2' },
    ],
  },
  {
    id: 'structure',
    label: 'Market Structure',
    insight: 'Dealers suppress moves. Range $620–$655 holds.',
    stats: [
      { val: 655,  dec: 0, pre: '$', suf: '',   label: 'call wall ceiling', color: '#34c759' },
      { val: 2.1,  dec: 1, pre: '+', suf: '%',  label: 'above flip',        color: '#bf5af2' },
      { val: 9.47, dec: 2, pre: '$', suf: '',   label: 'daily ATR',         color: '#ff9f0a' },
    ],
  },
  {
    id: 'flow',
    label: 'Dealer Flow',
    insight: 'Above $655, dealers are forced to sell. Mechanically.',
    stats: [
      { val: 38.4, dec: 1, pre: '$', suf: 'M', label: 'sell pressure',   color: '#ff453a' },
      { val: 22.1, dec: 1, pre: '$', suf: 'M', label: 'buy floor force', color: '#34c759' },
      { val: 655,  dec: 0, pre: '$', suf: '',  label: 'trigger level',   color: '#ff9f0a' },
    ],
  },
  {
    id: 'trap',
    label: 'Trap Zone',
    insight: '84% of gamma in 20 points. Something is about to break.',
    stats: [
      { val: 84, dec: 0, pre: '',  suf: '%',  label: 'gamma compressed', color: '#ff9f0a' },
      { val: 14, dec: 0, pre: '',  suf: '',   label: 'candles trapped',  color: '#ff453a' },
      { val: 20, dec: 0, pre: '',  suf: 'pt', label: 'squeeze band',     color: '#a1a1aa' },
    ],
  },
];

function ScCountUp({ to, decimals = 0, prefix = '', suffix = '', active, duration = 1100 }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) { setVal(0); return; }
    let s = null;
    const step = ts => {
      if (!s) s = ts;
      const p = Math.min((ts - s) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(parseFloat((to * e).toFixed(decimals)));
      if (p < 1) requestAnimationFrame(step);
      else setVal(to);
    };
    const id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [to, active, duration, decimals]);
  return <>{prefix}{decimals > 0 ? val.toFixed(decimals) : Math.round(val)}{suffix}</>;
}

function ScBar({ pct, color, delay = 0, active }) {
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!active) { setW(0); return; }
    const t = setTimeout(() => setW(pct), delay);
    return () => clearTimeout(t);
  }, [pct, delay, active]);
  return (
    <div style={{ height: '3px', background: 'rgba(255,255,255,0.05)', borderRadius: '2px', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${w}%`, background: `linear-gradient(90deg,${color}90,${color})`, borderRadius: '2px', transition: `width 1.1s cubic-bezier(0.22,1,0.36,1)`, boxShadow: `0 0 6px ${color}50` }} />
    </div>
  );
}

/* ── Mock content for each tab ── */
const MOCK_TICKERS = [
  { t: 'SPY',  spot: 648.57, regime: 'POS', cw: 655, flip: 635, pw: 620, near: false, atr: 9.47 },
  { t: 'QQQ',  spot: 512.34, regime: 'NEG', cw: 518, flip: 510, pw: 498, near: true,  atr: 8.23 },
  { t: 'NVDA', spot: 134.58, regime: 'POS', cw: 140, flip: 132, pw: 125, near: false, atr: 5.12 },
  { t: 'AAPL', spot: 249.58, regime: 'NEG', cw: 252, flip: 250, pw: 245, near: true,  atr: 4.21 },
];

function MockOverview({ active }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
      {MOCK_TICKERS.map((tk, i) => {
        const pos = tk.regime === 'POS';
        const c = pos ? '#34c759' : '#ff453a';
        return (
          <div key={tk.t} style={{ background: pos ? 'rgba(48,209,88,0.07)' : 'rgba(255,67,58,0.07)', border: `1px solid ${c}22`, borderTop: `2px solid ${c}`, borderRadius: '10px', padding: '12px', position: 'relative', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', opacity: active ? 1 : 0, transform: active ? 'none' : 'translateY(8px)', transition: `opacity .4s ease ${i * 80}ms, transform .4s ease ${i * 80}ms` }}>
            {tk.near && (
              <div style={{ position: 'absolute', top: 8, right: 8, width: 6, height: 6, borderRadius: '50%', background: '#ff9f0a', boxShadow: '0 0 8px rgba(255,159,10,0.8)', animation: 'sc-pulse 1.4s ease infinite' }} />
            )}
            <div style={{ fontSize: '15px', fontWeight: 900, color: '#fff', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '-0.3px' }}>{tk.t}</div>
            <div style={{ fontSize: '18px', fontWeight: 800, color: '#ff9f0a', fontFamily: "'JetBrains Mono',monospace", margin: '6px 0 8px', letterSpacing: '-0.5px' }}>
              $<ScCountUp to={tk.spot} decimals={2} active={active} duration={900} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              {[['CW', tk.cw, '#34c759'], ['FLIP', tk.flip, '#bf5af2'], ['PW', tk.pw, '#ff453a']].map(([l, v, col]) => (
                <div key={l}>
                  <div style={{ fontSize: '7px', color: col, fontFamily: 'monospace', letterSpacing: '0.5px', opacity: 0.7 }}>{l}</div>
                  <div style={{ fontSize: '11px', color: '#aeaeb2', fontFamily: 'monospace', fontWeight: 700 }}>${v}</div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: '8px' }}>
              <ScBar pct={pos ? 72 : 38} color={c} delay={300 + i * 100} active={active} />
            </div>
            <div style={{ fontSize: '8px', color: c, fontFamily: 'monospace', marginTop: '3px', fontWeight: 700, background: `${c}12`, border: `1px solid ${c}22`, borderRadius: '4px', padding: '2px 6px', display: 'inline-block' }}>
              {pos ? '+GEX' : '-GEX'}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MockStructure({ active }) {
  /* Line chart — Put/Call OI over time, matching PCOIChart in the dashboard */
  const DATA = [
    { d: 'Mar 1',  call: 28.2, put: 22.1 },
    { d: 'Mar 4',  call: 27.8, put: 23.4 },
    { d: 'Mar 5',  call: 29.1, put: 24.0 },
    { d: 'Mar 6',  call: 28.5, put: 25.8 },
    { d: 'Mar 7',  call: 27.2, put: 27.1 },
    { d: 'Mar 10', call: 26.8, put: 28.9 },
    { d: 'Mar 11', call: 27.5, put: 29.4 },
    { d: 'Mar 12', call: 26.1, put: 31.2 },
    { d: 'Mar 13', call: 24.8, put: 32.8 },
    { d: 'Mar 14', call: 25.3, put: 33.1 },
    { d: 'Mar 17', call: 26.8, put: 31.8 },
    { d: 'Mar 18', call: 28.4, put: 30.2 },
    { d: 'Mar 19', call: 29.8, put: 28.9 },
    { d: 'Mar 20', call: 31.2, put: 27.4 },
  ];
  const W = 540; const CH = 120; const PAD = 6;
  const allV = DATA.flatMap(d => [d.call, d.put]);
  const minV = Math.min(...allV) * 0.96;
  const maxV = Math.max(...allV) * 1.02;
  const xS = (i) => PAD + (i / (DATA.length - 1)) * (W - PAD * 2);
  const yS = (v) => (CH - 4) - ((v - minV) / (maxV - minV)) * (CH - 8);
  const linePath = (key) => DATA.map((d, i) => `${i === 0 ? 'M' : 'L'}${xS(i).toFixed(1)},${yS(d[key]).toFixed(1)}`).join(' ');
  const areaPath = (key) => `${linePath(key)} L${xS(DATA.length - 1).toFixed(1)},${CH} L${xS(0).toFixed(1)},${CH} Z`;
  const latest = DATA[DATA.length - 1];
  const pcRatio = (latest.put / latest.call).toFixed(2);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
        <div>
          <div style={{ fontSize: '9px', color: '#545459', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '8px' }}>Put / Call Open Interest · Daily</div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {['SPY', 'QQQ'].map((t, i) => (
              <div key={t} style={{ padding: '4px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: 600, fontFamily: "'JetBrains Mono',monospace", background: i === 0 ? '#f2f2f7' : 'rgba(255,255,255,0.06)', color: i === 0 ? '#111114' : '#545459' }}>{t}</div>
            ))}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '8px', color: '#545459', fontFamily: 'monospace', marginBottom: '2px', letterSpacing: '0.5px' }}>P/C RATIO</div>
          <div style={{ fontSize: '24px', fontWeight: 900, color: '#ff9f0a', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '-1px', lineHeight: 1 }}>{pcRatio}</div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '5px', justifyContent: 'flex-end' }}>
            {[['#34c759', 'Calls'], ['#ff453a', 'Puts']].map(([c, l]) => (
              <div key={l} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <div style={{ width: 12, height: 2.5, background: c, borderRadius: 2 }} />
                <span style={{ fontSize: '8px', color: '#545459', fontFamily: 'monospace' }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* SVG line chart */}
      <svg width="100%" viewBox={`0 0 ${W} ${CH}`} preserveAspectRatio="none" style={{ display: 'block', marginBottom: '2px' }}>
        <defs>
          <linearGradient id="sc-call-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34c759" stopOpacity={0.16} />
            <stop offset="100%" stopColor="#34c759" stopOpacity={0.01} />
          </linearGradient>
          <linearGradient id="sc-put-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ff453a" stopOpacity={0.14} />
            <stop offset="100%" stopColor="#ff453a" stopOpacity={0.01} />
          </linearGradient>
        </defs>
        {/* Horizontal grid */}
        {[0.3, 0.6, 0.9].map((p, i) => (
          <line key={i} x1={0} y1={CH * p} x2={W} y2={CH * p} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
        ))}
        {/* Area fills */}
        <path d={areaPath('call')} fill="url(#sc-call-fill)" />
        <path d={areaPath('put')} fill="url(#sc-put-fill)" />
        {/* Lines — self-drawing via strokeDashoffset */}
        <path d={linePath('call')} fill="none" stroke="#34c759" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ strokeDasharray: 900, strokeDashoffset: active ? 0 : 900, transition: active ? 'stroke-dashoffset 1.5s cubic-bezier(0.16,1,0.3,1) 0.08s' : 'none' }} />
        <path d={linePath('put')} fill="none" stroke="#ff453a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ strokeDasharray: 900, strokeDashoffset: active ? 0 : 900, transition: active ? 'stroke-dashoffset 1.5s cubic-bezier(0.16,1,0.3,1) 0.22s' : 'none' }} />
        {/* Endpoint dots */}
        <circle cx={xS(DATA.length - 1)} cy={yS(latest.call)} r="3.5" fill="#34c759"
          style={{ opacity: active ? 1 : 0, transition: 'opacity 0.3s ease 1.6s' }} />
        <circle cx={xS(DATA.length - 1)} cy={yS(latest.put)} r="3.5" fill="#ff453a"
          style={{ opacity: active ? 1 : 0, transition: 'opacity 0.3s ease 1.6s' }} />
      </svg>

      {/* Date labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 6px', marginBottom: '12px' }}>
        <span style={{ fontSize: '8px', color: '#3f3f46', fontFamily: 'monospace' }}>{DATA[0].d}</span>
        <span style={{ fontSize: '8px', color: '#3f3f46', fontFamily: 'monospace' }}>{DATA[DATA.length - 1].d}</span>
      </div>

      {/* Key chips */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '6px' }}>
        {[['Call OI', `${latest.call}M`, '#34c759'], ['Put OI', `${latest.put}M`, '#ff453a'], ['P/C Ratio', pcRatio, '#ff9f0a']].map(([l, v, c]) => (
          <div key={l} style={{ background: `${c}12`, border: `1px solid ${c}25`, borderRadius: '8px', padding: '8px 12px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
            <div style={{ fontSize: '8px', color: '#545459', fontFamily: 'monospace', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{l}</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: c, fontFamily: "'JetBrains Mono',monospace", letterSpacing: '-0.5px' }}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockDealerFlow({ active }) {
  return (
    <div style={{ background: 'rgba(22,22,24,0.6)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)', borderTop: '2px solid #34c759', borderRadius: '12px', padding: '20px', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#34c759" strokeWidth="2.5"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
        <span style={{ fontSize: '11px', fontWeight: 900, color: '#34c759', letterSpacing: '2px', fontFamily: 'monospace' }}>DEALER FLOW</span>
        <span style={{ fontSize: '10px', color: '#71717a', fontFamily: 'monospace', marginLeft: 'auto' }}>SPY · LIVE</span>
      </div>
      <p style={{ fontSize: '11px', color: '#71717a', margin: '0 0 18px', lineHeight: 1.5 }}>Mechanical hedging obligations above and below spot</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '18px' }}>
        {[
          { label: 'Above $655', action: 'SELL', mag: 38.4, color: '#ff453a', bar: 64, delay: 200 },
          { label: 'Below $635', action: 'BUY',  mag: 22.1, color: '#34c759', bar: 37, delay: 350 },
        ].map((row, i) => (
          <div key={i} style={{ background: `${row.color}10`, border: `1px solid ${row.color}22`, borderRadius: '10px', padding: '12px 14px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div>
                <span style={{ fontSize: '9px', color: row.color, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.5px' }}>{row.label}</span>
                <span style={{ fontSize: '9px', color: '#71717a', fontFamily: 'monospace', marginLeft: '8px' }}>dealers must {row.action}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '9px', color: '#71717a', fontFamily: 'monospace' }}>force</div>
                <div style={{ fontSize: '20px', fontWeight: 900, color: '#fff', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '-1px', lineHeight: 1 }}>
                  $<ScCountUp to={row.mag} decimals={1} active={active} duration={900} />M
                </div>
              </div>
            </div>
            <ScBar pct={row.bar} color={row.color} delay={row.delay} active={active} />
          </div>
        ))}
      </div>
      <div style={{ fontSize: '10px', color: '#636366', fontStyle: 'italic', lineHeight: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '12px' }}>
        Mechanical — not sentiment. Happens every time price moves.
      </div>
    </div>
  );
}

function MockTrapZone({ active }) {
  const spotPct = 68;
  const [dotPos, setDotPos] = useState(2);
  useEffect(() => {
    if (!active) { setDotPos(2); return; }
    const t = setTimeout(() => setDotPos(spotPct), 400);
    return () => clearTimeout(t);
  }, [active, spotPct]);
  return (
    <div style={{ background: 'rgba(22,22,24,0.6)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)', borderTop: '2px solid #ff9f0a', borderRadius: '12px', padding: '20px', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ff9f0a" strokeWidth="2.5"><polyline points="8 6 2 12 8 18"/><polyline points="16 6 22 12 16 18"/></svg>
          <span style={{ fontSize: '11px', fontWeight: 900, color: '#ff9f0a', letterSpacing: '2px', fontFamily: 'monospace' }}>TRAP ZONE</span>
        </div>
        <div style={{ background: 'rgba(255,159,10,0.1)', border: '1px solid rgba(255,159,10,0.25)', borderRadius: '5px', padding: '3px 8px', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#ff9f0a', animation: active ? 'sc-pulse 1.2s ease infinite' : 'none' }} />
          <span style={{ fontSize: '9px', color: '#ff9f0a', fontWeight: 700, fontFamily: 'monospace' }}>ACTIVE</span>
        </div>
      </div>
      <p style={{ fontSize: '11px', color: '#71717a', margin: '0 0 18px', lineHeight: 1.5 }}>84% of gamma compressed into a 20pt band — coiling</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '16px' }}>
        {[['CANDLES', '#ff9f0a'], ['COMPRESSION', '#ff9f0a']].map(([l, col], i) => (
          <div key={l} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '10px', padding: '10px 12px', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
            <div style={{ fontSize: '8px', color: '#8e8e93', fontFamily: 'monospace', letterSpacing: '1.5px', marginBottom: '4px' }}>{l}</div>
            <div style={{ fontSize: '24px', fontWeight: 900, color: col, fontFamily: "'JetBrains Mono',monospace", letterSpacing: '-1px' }}>
              {i === 0 ? <ScCountUp to={14} active={active} duration={700} /> : <><ScCountUp to={84} active={active} duration={1000} />%</>}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginBottom: '10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
          <span style={{ fontSize: '10px', color: '#ff453a', fontFamily: 'monospace', fontWeight: 700 }}>$635</span>
          <span style={{ fontSize: '9px', color: '#636366', fontFamily: 'monospace' }}>20pt range</span>
          <span style={{ fontSize: '10px', color: '#34c759', fontFamily: 'monospace', fontWeight: 700 }}>$655</span>
        </div>
        <div style={{ position: 'relative', height: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.04)', overflow: 'visible' }}>
          <div style={{ position: 'absolute', inset: 0, borderRadius: '4px', background: 'linear-gradient(90deg,rgba(255,69,58,.2),rgba(255,159,10,.08),rgba(48,209,88,.2))' }} />
          <div style={{ position: 'absolute', top: '50%', left: `${dotPos}%`, width: 14, height: 14, borderRadius: '50%', background: '#fff', border: '2px solid #05070e', boxShadow: '0 0 0 2px rgba(255,159,10,0.6), 0 2px 10px rgba(0,0,0,.6)', transform: 'translate(-50%,-50%)', transition: 'left .9s cubic-bezier(0.34,1.56,0.64,1)' }} />
        </div>
        <div style={{ textAlign: 'center', marginTop: '6px' }}>
          <span style={{ fontSize: '10px', color: '#ff9f0a', fontFamily: 'monospace', fontWeight: 700 }}>$648.57 spot</span>
        </div>
      </div>
      <div style={{ fontSize: '10px', color: '#636366', lineHeight: 1.6, borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '12px' }}>
        <span style={{ color: '#ebebf5', fontWeight: 700 }}>Watch $655.</span> Close above ends dealer resistance — sharp squeeze likely.
      </div>
    </div>
  );
}

/* ══ MASK-WORD REVEAL ══
   Each word slides up from behind a clip with spring + skewY physics.
   Uses @keyframes so it fires correctly on both initial reveal AND tab remount. */
function MaskWords({ text, active, baseDelay = 0, color = '#f2f2f7', fontSize = '22px', fontWeight = 700, spacing = '-0.4px', fontFamily }) {
  return (
    <div style={{ fontSize, fontWeight, color, letterSpacing: spacing, lineHeight: 1.15, ...(fontFamily ? { fontFamily } : {}) }}>
      {text.split(' ').map((w, i) => (
        <span key={i} style={{ display: 'inline-block', overflow: 'hidden', marginRight: '0.27em', verticalAlign: 'bottom' }}>
          <span style={{
            display: 'inline-block',
            opacity: active ? undefined : 0,
            animation: active ? `sc-mask-in 0.65s cubic-bezier(0.16,1,0.3,1) ${(baseDelay + i * 0.065).toFixed(3)}s both` : 'none',
          }}>{w}</span>
        </span>
      ))}
    </div>
  );
}


function ProductShowcaseSection({ onGetStarted }) {
  const [active, setActive]     = useState(0);
  const [animKey, setAnimKey]   = useState(0);
  const [entered, setEntered]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused]     = useState(false);
  const [ref, isVis]            = useReveal();
  const dragStart                = useRef(null);
  const activeRef                = useRef(0);
  const INTERVAL                 = 4000;

  useEffect(() => { if (isVis) setEntered(true); }, [isVis]);

  // Auto-advance with progress bar
  useEffect(() => {
    if (!entered || paused) { setProgress(0); return; }
    setProgress(0);
    const start = Date.now();
    const tick = setInterval(() => {
      const p = Math.min(((Date.now() - start) / INTERVAL) * 100, 100);
      setProgress(p);
      if (p >= 100) {
        const next = (activeRef.current + 1) % SHOWCASE_TABS.length;
        activeRef.current = next;
        setActive(next);
        setAnimKey(k => k + 1);
      }
    }, 30);
    return () => clearInterval(tick);
  }, [entered, paused, active]);

  const go = (i) => {
    const next = ((i % SHOWCASE_TABS.length) + SHOWCASE_TABS.length) % SHOWCASE_TABS.length;
    activeRef.current = next;
    setActive(next);
    setAnimKey(k => k + 1);
    setProgress(0);
  };

  // Swipe / drag support
  const onPointerDown = (e) => { dragStart.current = e.clientX; setPaused(true); };
  const onPointerUp   = (e) => {
    if (dragStart.current === null) return;
    const dx = e.clientX - dragStart.current;
    if (Math.abs(dx) > 40) go(active + (dx < 0 ? 1 : -1));
    dragStart.current = null;
    setPaused(false);
  };

  const tab = SHOWCASE_TABS[active];

  // Measure stage width for pixel-accurate card positioning
  const stageRef = useRef(null);
  const [stageW, setStageW] = useState(900);
  useEffect(() => {
    const update = () => { if (stageRef.current) setStageW(stageRef.current.offsetWidth); };
    update();
    const ro = new ResizeObserver(update);
    if (stageRef.current) ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, []);

  const isMobile = stageW < 640;
  const N      = SHOWCASE_TABS.length;
  const CARD_W = isMobile
    ? Math.round(stageW * 0.80)
    : Math.min(Math.round(stageW * 0.62), 740);
  const GAP  = isMobile ? 14 : 28;
  const STEP = CARD_W + GAP;

  // Shortest circular distance so cards always wrap round-robin
  const wrapOffset = (i) => {
    const raw = ((i - active) % N + N) % N;
    return raw > N / 2 ? raw - N : raw;
  };

  const getLeft  = (i) => stageW / 2 - CARD_W / 2 + wrapOffset(i) * STEP;

  const cardStyle = (i) => {
    const abs = Math.abs(wrapOffset(i));
    return {
      position: 'absolute', top: 0,
      left: `${getLeft(i)}px`,
      width: `${CARD_W}px`,
      height: isMobile ? '420px' : '420px',
      transform: `scale(${abs === 0 ? 1 : isMobile ? 0.93 : 0.86})`,
      transformOrigin: 'center center',
      opacity: abs === 0 ? 1 : abs === 1 ? 0.78 : 0,
      zIndex: 10 - abs,
      pointerEvents: abs <= 1 ? 'auto' : 'none',
      cursor: abs === 1 ? 'pointer' : 'default',
      transition: 'left 0.52s cubic-bezier(0.16,1,0.3,1), transform 0.52s cubic-bezier(0.16,1,0.3,1), opacity 0.4s ease',
    };
  };

  return (
    <section ref={ref} style={{ paddingTop: '96px', paddingBottom: '96px', borderTop: '1px solid rgba(255,255,255,0.09)' }}>
      <style>{`
        @keyframes sc-pulse     { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.35;transform:scale(.65)} }
        @keyframes sc-mask-in   { from{transform:translateY(108%) skewY(4deg)} to{transform:translateY(0) skewY(0deg)} }
        @keyframes sc-fade-word { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
        @keyframes sc-slide-in  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes sc-scan      { 0%{transform:translateY(-90px);opacity:0} 12%{opacity:1} 88%{opacity:1} 100%{transform:translateY(420px);opacity:0} }
      `}</style>

      {/* ── Header ── */}
      <div style={{ marginBottom: '48px', opacity: entered ? 1 : 0, transform: entered ? 'none' : 'translateY(16px)', transition: 'opacity .6s ease, transform .6s ease' }}>
        <div style={{ fontSize: '10px', color: '#71717a', fontFamily: "'JetBrains Mono',monospace", letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '14px' }}>Product tour</div>
        <h2 style={{ margin: 0, lineHeight: 1.1 }}>
          <MaskWords text="See the market through" active={entered} baseDelay={0}    fontSize="clamp(26px,3vw,44px)" fontWeight={700} spacing="-0.04em" color="#f2f2f7" fontFamily="'Plus Jakarta Sans Variable', system-ui, sans-serif" />
          <MaskWords text="a dealer's eyes"        active={entered} baseDelay={0.24} fontSize="clamp(26px,3vw,44px)" fontWeight={700} spacing="-0.04em" color="#34c759" fontFamily="'Plus Jakarta Sans Variable', system-ui, sans-serif" />
        </h2>
      </div>

      {/* ── Coverflow stage ── */}
      <div
        ref={stageRef}
        style={{ position: 'relative', height: '440px', overflow: 'hidden', userSelect: 'none' }}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* Left / right edge fade masks */}
        <div style={{ position: 'absolute', left: 0,  top: 0, bottom: 0, width: '7%', background: 'linear-gradient(to right, #0a0a0a, transparent)', zIndex: 20, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '7%', background: 'linear-gradient(to left,  #0a0a0a, transparent)', zIndex: 20, pointerEvents: 'none' }} />

        {/* Cards */}
        {SHOWCASE_TABS.map((t, i) => {
          const cs = cardStyle(i);
          const isAct = i === active;
          return (
            <div
              key={t.id}
              onClick={() => !isAct && go(i)}
              style={cs}
            >
              {/* Gradient border */}
              <div style={{ borderRadius: '18px', height: '100%', background: isAct ? 'linear-gradient(135deg,rgba(52,199,89,.22),rgba(48,168,255,.12),rgba(191,90,242,.06))' : 'rgba(255,255,255,0.13)', padding: '1px' }}>
                <div style={{ borderRadius: '17px', height: '100%', overflow: 'hidden', background: isAct ? 'rgba(12,12,14,0.95)' : 'rgba(22,22,27,0.97)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', display: 'flex', flexDirection: 'column' }}>
                  {/* Top accent */}
                  <div style={{ height: '2px', flexShrink: 0, background: isAct ? 'linear-gradient(90deg,#34c759,#30a8ff,rgba(48,168,255,0.1),transparent)' : 'rgba(255,255,255,0.06)' }} />
                  {/* Label bar */}
                  <div style={{ padding: '14px 20px 10px', flexShrink: 0, borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '9px', color: isAct ? '#34c759' : '#3f3f46', fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, letterSpacing: '1px' }}>{String(i + 1).padStart(2, '0')}</span>
                    <span style={{ width: '1px', height: '12px', background: 'rgba(255,255,255,0.1)' }} />
                    <span style={{ fontSize: '13px', fontWeight: 600, color: isAct ? '#f2f2f7' : '#71717a', letterSpacing: '-0.1px' }}>{t.label}</span>
                    {isAct && !isMobile && <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#545459', fontStyle: 'italic', fontFamily: "'JetBrains Mono',monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '40%' }}>{t.insight}</span>}
                  </div>
                  {/* Mock content — always show, re-animate only active */}
                  <div style={{ flex: 1, padding: '20px', overflow: 'hidden' }} key={isAct ? animKey : i}>
                    {i === 0 && <MockOverview   active={entered} />}
                    {i === 1 && <MockStructure  active={entered} />}
                    {i === 2 && <MockDealerFlow active={entered} />}
                    {i === 3 && <MockTrapZone   active={entered} />}
                  </div>
                  {/* Auto-advance progress bar */}
                  <div style={{ height: '2px', flexShrink: 0, background: 'rgba(255,255,255,0.05)' }}>
                    {isAct && <div style={{ height: '100%', width: `${progress}%`, background: 'linear-gradient(90deg,#34c759,#30a8ff)', transition: 'width 0.03s linear' }} />}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Tab pills nav ── */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '28px', flexWrap: 'wrap', opacity: entered ? 1 : 0, transition: 'opacity .5s ease .3s' }}>
        {SHOWCASE_TABS.map((t, i) => {
          const isAct = i === active;
          return (
            <button key={t.id} onClick={() => { go(i); setPaused(false); }} style={{
              padding: '8px 18px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: '13px', fontWeight: isAct ? 600 : 400,
              background: isAct ? 'rgba(52,199,89,0.1)' : 'transparent',
              border: isAct ? '1px solid rgba(52,199,89,0.3)' : '1px solid rgba(255,255,255,0.08)',
              color: isAct ? '#34c759' : '#71717a',
              transition: 'all 0.2s ease',
            }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Dot indicators ── */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '16px' }}>
        {SHOWCASE_TABS.map((_, i) => (
          <button key={i} onClick={() => go(i)} style={{ width: i === active ? '20px' : '6px', height: '6px', borderRadius: '3px', background: i === active ? '#34c759' : 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.35s cubic-bezier(0.16,1,0.3,1)' }} />
        ))}
      </div>

      {/* ── Stat chips + CTA ── */}
      <div key={`bottom-${animKey}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '28px', flexWrap: 'wrap', justifyContent: 'center', opacity: entered ? 1 : 0, transition: 'opacity .4s ease .2s' }}>
        {tab.stats.map((s, i) => (
          <div key={i} style={{
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '12px', padding: '10px 18px', textAlign: 'center',
            animation: entered ? `sc-slide-in 0.4s cubic-bezier(0.16,1,0.3,1) ${(i * 0.08).toFixed(2)}s both` : 'none',
          }}>
            <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '20px', fontWeight: 900, color: s.color, letterSpacing: '-0.8px', lineHeight: 1 }}>
              {s.pre}<ScCountUp to={s.val} decimals={s.dec} active={entered} duration={900} />{s.suf}
            </div>
            <div style={{ fontSize: '9px', color: '#71717a', marginTop: '4px', letterSpacing: '0.3px', textTransform: 'uppercase' }}>{s.label}</div>
          </div>
        ))}
        <button className="gl-btn-primary" onClick={onGetStarted}
          style={{ padding: '11px 26px', background: '#f2f2f7', border: 'none', borderRadius: '10px', color: '#111114', fontSize: '13px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginLeft: '8px' }}>
          Open Dashboard →
        </button>
      </div>
    </section>
  );
}

export { GammaLensLogo };
